'use client';

import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { AppLink } from '@/lib/appLink';

const DEFAULT_SLIDES = [
  {
    id: 'budivnytstvo',
    tag: 'Будівництво',
    title: 'Будинки\nпід ключ',
    subtitle:
      'Зводимо приватні будинки 1–1.5 поверху з нуля — від закладки фундаменту до фінального оздоблення. Якість, терміни, гарантія.',
    cta: 'Дізнатись більше',
    href: '/budivnytstvo',
    image_url:
      'https://media.base44.com/images/public/6a1311bd1062b12420e3c449/83ab24b3a_1779640495370728.jpg',
    image_position: 'center',
  },
  {
    id: 'fasad',
    tag: 'Фасадні роботи',
    title: 'Утеплення та\nоздоблення фасадів',
    subtitle:
      'Сучасні фасади з утепленням, облицюванням камінням та штукатуркою для збереження тепла та естетики',
    cta: 'Фасадні роботи',
    href: '/fasad',
    image_url:
      'https://media.base44.com/images/public/6a1311bd1062b12420e3c449/183fa433a_1779640019874869.jpg',
    image_position: 'center 30%',
  },
  {
    id: 'remont',
    tag: 'Ремонт',
    title: 'Ремонт\nта реконструкція',
    subtitle: 'Капітальний ремонт квартир і приміщень',
    cta: 'Ремонтні роботи',
    href: '/remont',
    image_url:
      'https://media.base44.com/images/public/6a1311bd1062b12420e3c449/e95b94e13_generated_image.png',
    image_position: 'center',
  },
];

function hasMarkup(title = '') {
  return /<\/?(?:span|br)\b/i.test(title);
}

function titleToHtml(title = '') {
  return String(title).replace(/\r\n|\r|\n/g, '<br />');
}

/**
 * @param {{ title?: string }} props
 */
function HeroTitle({ title = '' }) {
  if (!title) return null;

  if (hasMarkup(title)) {
    return (
      <h1
        className="grv-hero-title text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
        dangerouslySetInnerHTML={{ __html: titleToHtml(title) }}
      />
    );
  }

  return (
    <h1 className="grv-hero-title text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 whitespace-pre-line">
      {title}
    </h1>
  );
}

/**
 * Hero slider — SSR markup + ThemeScript (swipe / dots / autoplay without React hydration).
 *
 * @param {{ section?: import('@/types/cms').HeroSliderPageSection }} props
 */
export default function HeroSlider({ section = {} }) {
  const slides = useMemo(() => {
    const source = section.slides?.length ? section.slides : DEFAULT_SLIDES;
    return source.map((slide, index) => ({
      id: slide.id || `slide-${index}`,
      tag: slide.tag || '',
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      cta: (slide.cta || '').trim(),
      href: slide.href || '/',
      image: slide.image_url || slide.imageUrl || '',
      imagePosition: slide.image_position || slide.imagePosition || 'center',
    }));
  }, [section.slides]);

  const heightMode = section.height_mode === 'custom' ? 'custom' : 'full';
  const minHeight = section.min_height || 640;
  const secondaryLabel = (section.secondary_button_label || '').trim();
  const secondaryHref = section.secondary_button_href || '/contacts';
  const showSecondary = Boolean(secondaryLabel);

  const sectionStyle =
    heightMode === 'full'
      ? { minHeight: '100svh' }
      : { minHeight: `${minHeight}px` };

  const sectionClass =
    heightMode === 'full'
      ? 'grv-hero relative h-screen min-h-[100svh] overflow-hidden bg-dark'
      : 'grv-hero relative overflow-hidden bg-dark';

  if (!slides.length) {
    return null;
  }

  const multi = slides.length > 1;

  return (
    <section
      className={sectionClass}
      style={sectionStyle}
      data-grv-hero
      data-grv-hero-index="0"
      data-grv-hero-count={String(slides.length)}
      data-grv-hero-interval="6000"
    >
      {slides.map((s, i) => (
        <div
          key={`bg-${s.id}`}
          data-grv-hero-bg
          data-index={String(i)}
          className={`grv-hero-bg absolute inset-0 bg-cover transition-opacity duration-700${i === 0 ? ' is-active' : ''}`}
          style={{
            backgroundImage: s.image ? `url(${s.image})` : undefined,
            backgroundPosition: s.imagePosition,
          }}
          aria-hidden={i === 0 ? 'false' : 'true'}
        />
      ))}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(135deg, rgba(20,20,20,0.92) 0%, rgba(20,20,20,0.75) 35%, rgba(20,20,20,0.25) 60%, rgba(20,20,20,0.05) 100%)',
        }}
      />

      <div
        className="relative z-10 flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-12"
        style={heightMode === 'full' ? { height: '100%' } : { minHeight: `${minHeight}px` }}
      >
        <div className="relative w-full max-w-xl grv-hero-panels">
          {slides.map((slide, i) => {
            const showPrimary = Boolean(slide.cta);
            const showButtons = showPrimary || showSecondary;
            return (
              <div
                key={`panel-${slide.id}`}
                data-grv-hero-panel
                data-index={String(i)}
                className={`grv-hero-panel transition-all duration-500${i === 0 ? ' is-active' : ''}`}
                aria-hidden={i === 0 ? 'false' : 'true'}
              >
                {slide.tag ? (
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-px bg-gold" />
                    <span className="text-gold text-xs font-semibold uppercase tracking-widest">{slide.tag}</span>
                  </div>
                ) : null}

                <HeroTitle title={slide.title} />

                <div className="w-16 h-0.5 bg-gold-gradient mb-4" />

                {slide.subtitle ? (
                  <p className="text-gray-300 text-lg mb-10 max-w-md">{slide.subtitle}</p>
                ) : null}

                {showButtons ? (
                  <div className="flex flex-col sm:flex-row items-center gap-4 max-w-md">
                    {showPrimary ? (
                      <AppLink
                        href={slide.href}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold-gradient text-dark font-bold px-8 py-4 rounded hover:opacity-90 transition-opacity text-sm uppercase tracking-wider whitespace-nowrap"
                      >
                        {slide.cta} <ArrowRight className="w-4 h-4" />
                      </AppLink>
                    ) : null}
                    {showSecondary ? (
                      <AppLink
                        href={secondaryHref}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:border-gold hover:text-gold font-semibold px-8 py-4 rounded transition-colors text-sm uppercase tracking-wider"
                      >
                        {secondaryLabel}
                      </AppLink>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {multi ? (
        <>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={`dot-${i}`}
                type="button"
                data-grv-hero-dot
                data-index={String(i)}
                className={`grv-hero-dot h-1 rounded-full transition-all duration-300${
                  i === 0 ? ' is-active w-8 bg-white' : 'w-2 bg-white/40'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <div className="absolute right-6 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-3">
            {slides.map((_, i) => (
              <button
                key={`side-${i}`}
                type="button"
                data-grv-hero-side
                data-index={String(i)}
                className={`grv-hero-side rounded-full transition-all duration-300${
                  i === 0 ? ' is-active bg-gold w-3 h-3' : 'w-2 h-2 bg-white/30'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 z-20 overflow-hidden">
            <div className="grv-hero-progress h-full bg-gold-gradient" data-grv-hero-progress />
          </div>
        </>
      ) : null}
    </section>
  );
}
